import { useTranslation } from "react-i18next";
import type { CreditBalanceCardProps } from "./types";

const LOW_CREDIT_THRESHOLD = 0.2;

const getProgressBarColor = (available: number, total: number) => {
  if (available <= 0) return "bg-red-500";
  if (available <= total * LOW_CREDIT_THRESHOLD) return "bg-amber-500";
  return "bg-emerald-500";
};

const getProgressBarWidth = (available: number, total: number) => {
  if (total <= 0) return "0%";
  return `${(available / total) * 100}%`;
};

export const CreditBalanceCard = ({
  creditBalance,
  isLoading,
}: CreditBalanceCardProps) => {
  const { t } = useTranslation();

  return (
    <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">
          {t("profile.credits.title")}
        </h2>
        {isLoading && (
          <p className="text-sm text-slate-500">
            {t("profile.credits.loading")}
          </p>
        )}
        {creditBalance && (
          <div>
            <div className="flex items-end justify-between mb-3">
              <div>
                <p className="text-3xl font-bold text-slate-900">
                  {creditBalance.available}
                </p>
                <p className="text-sm text-slate-500">
                  {t("profile.credits.available")}
                </p>
              </div>
              <p className="text-sm text-slate-400">
                {creditBalance.usedCredits} / {creditBalance.totalCredits}{" "}
                {t("profile.credits.used")}
              </p>
            </div>
            <div className="h-2.5 w-full rounded-full bg-slate-100 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${getProgressBarColor(creditBalance.available, creditBalance.totalCredits)}`}
                style={{
                  width: getProgressBarWidth(
                    creditBalance.available,
                    creditBalance.totalCredits,
                  ),
                }}
              />
            </div>
            {creditBalance.available <= 0 && (
              <p className="mt-3 text-sm text-red-600 font-medium">
                {t("profile.credits.noCreditsWarning")}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
